import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import type { CompatibilityRow } from '@/lib/vehicleTypes';
import { Plus, Save, X, Edit, Trash2 } from 'lucide-react';
import VehicleSlotColourSettings from './VehicleSlotColourSettings';

interface CapacitySetting {
  id: string;
  tenant_id: string;
  vehicle_type: string;
  icon_style: 'car' | 'truck';
  colour_hex: string;
  slot_length_minutes: number;
  load_buffer_minutes: number;
  unload_buffer_minutes: number;
  return_buffer_required: boolean;
  return_buffer_trigger: 'service_area' | 'drive_time';
  return_buffer_minutes: number;
  drive_time_threshold_minutes: number;
  min_spacing_minutes: number;
  max_jobs_per_period: number | null;
  is_active: boolean;
  sort_order: number;
}

const DEFAULT_COLORS = [
  '#3B82F6', '#6366F1', '#10B981', '#8B5CF6', '#EF4444',
  '#F97316', '#EC4899', '#14B8A6', '#F59E0B', '#64748B',
];

const DEFAULT_NEW_FORM: Partial<CapacitySetting> = {
  vehicle_type: '',
  icon_style: 'car',
  colour_hex: '#3B82F6',
  slot_length_minutes: 15,
  load_buffer_minutes: 3,
  unload_buffer_minutes: 3,
  return_buffer_required: false,
  return_buffer_trigger: 'drive_time',
  return_buffer_minutes: 15,
  drive_time_threshold_minutes: 20,
  min_spacing_minutes: 5,
  max_jobs_per_period: null,
  is_active: true,
  sort_order: 100,
};

const VehicleTypeCapacitySettings: React.FC = () => {
  const [settings, setSettings] = useState<CapacitySetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<CapacitySetting>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState<Partial<CapacitySetting>>(DEFAULT_NEW_FORM);
  const [saving, setSaving] = useState(false);
  const [compatibility, setCompatibility] = useState<CompatibilityRow[]>([]);
  const [compatSaving, setCompatSaving] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
    loadCompatibility();
  }, []);

  const loadCompatibility = async () => {
    const { data, error } = await supabase
      .from('vehicle_type_compatibility')
      .select('vehicle_type, can_serve_job_type');
    if (error) {
      console.error('Error loading vehicle type compatibility:', error);
    } else {
      setCompatibility((data as CompatibilityRow[]) || []);
    }
  };

  const isCompatible = (vehicleType: string, jobType: string) =>
    vehicleType === jobType || compatibility.some((c) => c.vehicle_type === vehicleType && c.can_serve_job_type === jobType);

  const toggleCompatibility = async (vehicleType: string, jobType: string) => {
    if (vehicleType === jobType) return; // self-serves-self, always on
    const cellKey = `${vehicleType}::${jobType}`;
    const currentlyOn = compatibility.some((c) => c.vehicle_type === vehicleType && c.can_serve_job_type === jobType);
    setCompatSaving(cellKey);
    if (currentlyOn) {
      const { error } = await supabase
        .from('vehicle_type_compatibility')
        .delete()
        .eq('vehicle_type', vehicleType)
        .eq('can_serve_job_type', jobType);
      if (error) {
        toast({ title: 'Error', description: 'Failed to update: ' + error.message, variant: 'destructive' });
      } else {
        setCompatibility((prev) => prev.filter((c) => !(c.vehicle_type === vehicleType && c.can_serve_job_type === jobType)));
      }
    } else {
      const tenantRow = settings.find((s) => s.vehicle_type === vehicleType);
      const { error } = await supabase.from('vehicle_type_compatibility').insert({
        tenant_id: tenantRow?.tenant_id,
        vehicle_type: vehicleType,
        can_serve_job_type: jobType,
      });
      if (error) {
        toast({ title: 'Error', description: 'Failed to update: ' + error.message, variant: 'destructive' });
      } else {
        setCompatibility((prev) => [...prev, { vehicle_type: vehicleType, can_serve_job_type: jobType }]);
      }
    }
    setCompatSaving(null);
  };

  const loadSettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('vehicle_types')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error loading capacity settings:', error);
      toast({ title: 'Error', description: 'Failed to load vehicle capacity settings', variant: 'destructive' });
    } else {
      setSettings(data || []);
    }
    setLoading(false);
  };

  const startEdit = (s: CapacitySetting) => {
    setEditingId(s.id);
    setEditForm({ ...s });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const buildPayload = (form: Partial<CapacitySetting>) => ({
    icon_style: form.icon_style ?? 'car',
    colour_hex: form.colour_hex ?? '#3B82F6',
    slot_length_minutes: form.slot_length_minutes ?? 15,
    load_buffer_minutes: form.load_buffer_minutes ?? 3,
    unload_buffer_minutes: form.unload_buffer_minutes ?? 3,
    return_buffer_required: form.return_buffer_required ?? false,
    return_buffer_trigger: form.return_buffer_trigger ?? 'drive_time',
    return_buffer_minutes: form.return_buffer_minutes ?? 15,
    drive_time_threshold_minutes: form.drive_time_threshold_minutes ?? 20,
    min_spacing_minutes: form.min_spacing_minutes ?? 5,
    max_jobs_per_period: form.max_jobs_per_period ?? null,
    is_active: form.is_active ?? true,
    sort_order: form.sort_order ?? 100,
    updated_at: new Date().toISOString(),
  });

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.vehicle_type?.trim()) {
      toast({ title: 'Error', description: 'Vehicle type name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);

    const originalType = settings.find((s) => s.id === editingId)?.vehicle_type;
    const newType = editForm.vehicle_type.trim();

    if (originalType && newType !== originalType) {
      const { error: renameError } = await supabase.rpc('rename_vehicle_type', {
        p_vehicle_type_id: editingId,
        p_new_name: newType,
      });
      if (renameError) {
        toast({ title: 'Error', description: 'Failed to rename: ' + renameError.message, variant: 'destructive' });
        setSaving(false);
        return;
      }
    }

    const { error } = await supabase
      .from('vehicle_types')
      .update(buildPayload(editForm))
      .eq('id', editingId);

    if (error) {
      toast({ title: 'Error', description: 'Failed to update: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `${newType} capacity settings updated` });
      setEditingId(null);
      setEditForm({});
      loadSettings();
      loadCompatibility();
    }
    setSaving(false);
  };

  const addSetting = async () => {
    if (!newForm.vehicle_type?.trim()) {
      toast({ title: 'Error', description: 'Vehicle type is required', variant: 'destructive' });
      return;
    }
    if (settings.some((s) => s.vehicle_type.toLowerCase() === newForm.vehicle_type?.trim().toLowerCase())) {
      toast({ title: 'Error', description: 'This vehicle type already has capacity settings', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('vehicle_types').insert({
      vehicle_type: newForm.vehicle_type.trim(),
      ...buildPayload(newForm),
    });

    if (error) {
      toast({ title: 'Error', description: 'Failed to add: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `${newForm.vehicle_type} capacity settings added` });
      setShowAddForm(false);
      setNewForm(DEFAULT_NEW_FORM);
      loadSettings();
    }
    setSaving(false);
  };

  const deleteSetting = async (s: CapacitySetting) => {
    if (!confirm(`Delete capacity settings for "${s.vehicle_type}"? This cannot be undone.`)) return;
    const { error } = await supabase.from('vehicle_types').delete().eq('id', s.id);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Deleted', description: `${s.vehicle_type} capacity settings removed` });
      loadSettings();
    }
  };

  const renderForm = (form: Partial<CapacitySetting>, setForm: (f: Partial<CapacitySetting>) => void, isNew: boolean) => (
    <div className="space-y-4 p-4 rounded-lg border border-gray-600 bg-gray-800/50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-yellow-500 text-xs">Vehicle Type</Label>
          <Input
            value={form.vehicle_type || ''}
            onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
            placeholder="e.g. Sedan, Wheelchair, SUV"
          />
          {!isNew && (
            <p className="text-[10px] text-gray-500 mt-1">
              Renaming here updates every vehicle, roster shift type, and compatibility rule using this type.
            </p>
          )}
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Icon Style</Label>
          <Select
            value={form.icon_style ?? 'car'}
            onValueChange={(v) => setForm({ ...form, icon_style: v as 'car' | 'truck' })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="car">Car</SelectItem>
              <SelectItem value="truck">Truck / Van</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Colour</Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="color"
              value={form.colour_hex || '#3B82F6'}
              onChange={(e) => setForm({ ...form, colour_hex: e.target.value })}
              className="w-9 h-9 rounded cursor-pointer border border-gray-600 bg-transparent"
            />
            <div className="flex gap-1 flex-wrap">
              {DEFAULT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, colour_hex: c })}
                  className={`w-5 h-5 rounded-full border-2 transition-all ${form.colour_hex === c ? 'border-white scale-110' : 'border-transparent hover:border-gray-400'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Slot Length (min)</Label>
          <Input
            type="number"
            value={form.slot_length_minutes ?? 15}
            onChange={(e) => setForm({ ...form, slot_length_minutes: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Load Buffer (min)</Label>
          <Input
            type="number"
            value={form.load_buffer_minutes ?? 3}
            onChange={(e) => setForm({ ...form, load_buffer_minutes: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Unload Buffer (min)</Label>
          <Input
            type="number"
            value={form.unload_buffer_minutes ?? 3}
            onChange={(e) => setForm({ ...form, unload_buffer_minutes: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Min Spacing Between Jobs (min)</Label>
          <Input
            type="number"
            value={form.min_spacing_minutes ?? 5}
            onChange={(e) => setForm({ ...form, min_spacing_minutes: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Max Jobs Per Period</Label>
          <Input
            type="number"
            value={form.max_jobs_per_period ?? ''}
            placeholder="No limit"
            onChange={(e) => setForm({ ...form, max_jobs_per_period: e.target.value ? parseInt(e.target.value) : null })}
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Sort Order</Label>
          <Input
            type="number"
            value={form.sort_order ?? 100}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })}
          />
        </div>
      </div>

      {/* Return buffer */}
      <div className="space-y-3 pt-2 border-t border-gray-700">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`return-required-${isNew ? 'new' : 'edit'}`}
            checked={form.return_buffer_required ?? false}
            onCheckedChange={(c) => setForm({ ...form, return_buffer_required: c as boolean })}
          />
          <Label htmlFor={`return-required-${isNew ? 'new' : 'edit'}`} className="text-sm">
            Return-to-service-area buffer required
          </Label>
        </div>

        {form.return_buffer_required && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-6">
            <div>
              <Label className="text-yellow-500 text-xs">Trigger</Label>
              <Select
                value={form.return_buffer_trigger ?? 'drive_time'}
                onValueChange={(v) => setForm({ ...form, return_buffer_trigger: v as 'service_area' | 'drive_time' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="drive_time">Drive time threshold</SelectItem>
                  <SelectItem value="service_area">Service area (zone) — coming soon</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-yellow-500 text-xs">Return Buffer (min)</Label>
              <Input
                type="number"
                value={form.return_buffer_minutes ?? 15}
                onChange={(e) => setForm({ ...form, return_buffer_minutes: parseInt(e.target.value) || 0 })}
              />
            </div>
            {form.return_buffer_trigger !== 'service_area' && (
              <div>
                <Label className="text-yellow-500 text-xs">Drive Time Threshold (min)</Label>
                <Input
                  type="number"
                  value={form.drive_time_threshold_minutes ?? 20}
                  onChange={(e) => setForm({ ...form, drive_time_threshold_minutes: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-gray-700">
        <Checkbox
          id={`active-${isNew ? 'new' : 'edit'}`}
          checked={form.is_active ?? true}
          onCheckedChange={(c) => setForm({ ...form, is_active: c as boolean })}
        />
        <Label htmlFor={`active-${isNew ? 'new' : 'edit'}`} className="text-xs">Active</Label>
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={isNew ? addSetting : saveEdit} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Saving...' : isNew ? 'Add Vehicle Type' : 'Save Changes'}
        </Button>
        <Button onClick={isNew ? () => setShowAddForm(false) : cancelEdit} variant="outline" size="sm">
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-yellow-500">Vehicle Types</h2>
          <p className="text-sm text-gray-400 mt-1">
            The single source of truth for vehicle types — used when creating a vehicle, setting up roster shift types, and the Fleet Capacity view. Icon, colour, and capacity rules (slot length, load/unload buffers, return-to-zone buffer, spacing) are all set here, together.
          </p>
        </div>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle Type
          </Button>
        )}
      </div>

      {showAddForm && renderForm(newForm, setNewForm, true)}

      <div className="space-y-2">
        {settings.map((s) => (
          <div key={s.id}>
            {editingId === s.id ? (
              renderForm(editForm, setEditForm, false)
            ) : (
              <Card className={`p-3 ${s.is_active ? 'border-gray-700 bg-gray-800/30 hover:bg-gray-800/60' : 'border-gray-800 bg-gray-900/30 opacity-60'}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0 border border-gray-600"
                    style={{ backgroundColor: s.colour_hex || '#3B82F6' }}
                  />
                  <Badge variant="outline" className="font-mono text-xs flex-shrink-0">
                    {s.vehicle_type}
                  </Badge>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">{s.icon_style === 'truck' ? 'Truck/Van icon' : 'Car icon'}</span>
                  <span className="text-xs text-gray-300">Slot: {s.slot_length_minutes}min</span>
                  <span className="text-xs text-gray-300">Load: {s.load_buffer_minutes}min</span>
                  <span className="text-xs text-gray-300">Unload: {s.unload_buffer_minutes}min</span>
                  <span className="text-xs text-gray-300">Spacing: {s.min_spacing_minutes}min</span>
                  {s.return_buffer_required && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Return buffer: {s.return_buffer_minutes}min ({s.return_buffer_trigger === 'drive_time' ? `>${s.drive_time_threshold_minutes}min drive` : 'zone'})
                    </Badge>
                  )}
                  {s.max_jobs_per_period != null && (
                    <span className="text-xs text-gray-400">Max jobs/period: {s.max_jobs_per_period}</span>
                  )}
                  {!s.is_active && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Inactive</Badge>}

                  <div className="flex gap-1 ml-auto flex-shrink-0">
                    <button onClick={() => startEdit(s)} className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteSetting(s)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        ))}

        {settings.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No vehicle types configured. Click "Add Vehicle Type" to create one.
          </div>
        )}
      </div>

      {/* Compatibility matrix */}
      {settings.length > 1 && (
        <Card className="p-4 border-gray-700 bg-gray-800/30">
          <h3 className="text-lg font-bold text-yellow-500">Vehicle Type Compatibility</h3>
          <p className="text-sm text-gray-400 mt-1 mb-4">
            Which vehicle types can cover which job requirements — e.g. an SUV-profiled job can be covered by a Car or an SUV, but a Car-profiled job can only be covered by a Car. A type always covers its own profile. Used by future IVR/booking matching.
          </p>
          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr>
                  <th className="text-left text-xs text-gray-400 font-normal pr-4 pb-2">Vehicle ↓ / Can cover job →</th>
                  {settings.map((s) => (
                    <th key={s.id} className="text-xs text-gray-400 font-normal px-3 pb-2 text-center">{s.vehicle_type}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {settings.map((rowType) => (
                  <tr key={rowType.id}>
                    <td className="text-gray-200 pr-4 py-1.5 whitespace-nowrap">{rowType.vehicle_type}</td>
                    {settings.map((colType) => {
                      const isSelf = rowType.vehicle_type === colType.vehicle_type;
                      const cellKey = `${rowType.vehicle_type}::${colType.vehicle_type}`;
                      return (
                        <td key={colType.id} className="text-center px-3 py-1.5">
                          <Checkbox
                            checked={isCompatible(rowType.vehicle_type, colType.vehicle_type)}
                            disabled={isSelf || compatSaving === cellKey}
                            onCheckedChange={() => toggleCompatibility(rowType.vehicle_type, colType.vehicle_type)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {settings.length > 0 && (
        <VehicleSlotColourSettings vehicleTypes={settings.map((s) => ({ vehicle_type: s.vehicle_type, tenant_id: s.tenant_id }))} />
      )}
    </div>
  );
};

export default VehicleTypeCapacitySettings;
